import { useCallback } from "react";
import { useMutation } from "react-query";

import type { OtpChannel } from "@jti/fe-common";
import type { PhoneValidationInitResponse, PvsResponse, StatusResponse } from "@jti/rest-api-types";

import { MUTATION_KEYS } from "../../constants/queryKeys";
import { PvsApi } from "../../services";

type PhoneService = {
  verifyOtp: (phoneVerificationUuid: string, code: string) => Promise<PvsResponse>;
  verifyEmailOtp: (uuid: string, code: string) => Promise<PvsResponse>;
  checkValidationStatus: (uuid: string, hash: string) => Promise<StatusResponse>;
  initPhoneValidationByOtp: (
    phone: string,
    channel: OtpChannel,
    otpTemplate?: string
  ) => Promise<PhoneValidationInitResponse>;
  isVerifyOtpLoading: boolean;
  isVerifyOtpFailed: boolean;
  isVerifyEmailOtpLoading: boolean;
};

export const usePhoneService = (): PhoneService => {
  const pvsApi = PvsApi.init();
  const callVerifyOtp = useMutation(
    MUTATION_KEYS.verifyOtp,
    ({ phoneVerificationUuid, code }: { phoneVerificationUuid: string; code: string }) =>
      pvsApi.verifyOtp(phoneVerificationUuid, code)
  );

  const verifyOtp = useCallback(
    async (phoneVerificationUuid: string, code: string) =>
      await callVerifyOtp.mutateAsync({ phoneVerificationUuid, code }),
    [callVerifyOtp]
  );

  const callVerifyEmailOtp = useMutation(
    MUTATION_KEYS.verifyEmailOtp,
    ({ uuid, code }: { uuid: string; code: string }) => pvsApi.verifyEmailOtp(uuid, code)
  );

  const verifyEmailOtp = useCallback(
    async (uuid: string, code: string) => await callVerifyEmailOtp.mutateAsync({ uuid, code }),
    [callVerifyEmailOtp]
  );

  const checkValidationStatusMutation = useMutation(
    MUTATION_KEYS.checkValidationStatus,
    ({ uuid, hash }: { uuid: string; hash: string }) => pvsApi.checkVerificationStatus(uuid, hash)
  );

  const checkValidationStatus = useCallback(
    async (uuid: string, hash: string) => await checkValidationStatusMutation.mutateAsync({ uuid, hash }),
    [checkValidationStatusMutation]
  );

  const initPhoneValidationByOtpMutation = useMutation(
    MUTATION_KEYS.initPhoneValidationByOtp,
    ({ phone, otpChannel, otpTemplate }: { phone: string; otpChannel: OtpChannel; otpTemplate?: string }) =>
      pvsApi.initPhoneValidationByOtp(phone, otpChannel, otpTemplate)
  );

  const initPhoneValidationByOtp = useCallback(
    async (phone: string, otpChannel: OtpChannel, otpTemplate?: string) =>
      await initPhoneValidationByOtpMutation.mutateAsync({ phone, otpChannel, otpTemplate }),
    [initPhoneValidationByOtpMutation]
  );

  return {
    verifyOtp,
    verifyEmailOtp,
    isVerifyEmailOtpLoading: callVerifyEmailOtp.isLoading,
    checkValidationStatus,
    initPhoneValidationByOtp,
    isVerifyOtpLoading: callVerifyOtp.isLoading,
    isVerifyOtpFailed: callVerifyOtp.isError,
  };
};
